/**
 * Loyalty Sales Workspace — Phase 3B (P0 · multi-outlet repair)
 *
 * Mobile-first transactional sales app for the loyalty_sales role.
 * Admins / area_managers / super_admins can also use it.
 *
 *   Route:  /sales/loyalty?outlet_id=<selected_outlet_id>
 *   Roles:  admin, area_manager, super_admin, loyalty_sales
 *
 * P0 changes (this iteration):
 *   • Mandatory outlet selection for multi-outlet sales users.
 *   • URL (?outlet_id=…) is the source of truth. Persisted per-user
 *     in localStorage["restropulse_sales_outlet::<uid>"].
 *   • "Selling at <Outlet Name>" + Change Outlet in every header.
 *   • All catalogue + transaction calls carry outlet_id; no /auth/me
 *     fallback anywhere in the checkout flow.
 *   • Create Member sends primary_outlet_id = selectedSalesOutlet.id.
 *   • Neutral "Member Profile" chip until an actual membership card
 *     is proven — no more misleading "Active Member" for a bare
 *     profile.
 *   • Top-level exit: "← Back to RestroPulse" for admin-family or
 *     "Sales Home / Change Outlet" for loyalty_sales — no logout
 *     required to escape the flow.
 *   • Empty catalogue state names the outlet and points Admin at the
 *     "Available to Sales Team" toggle.
 *
 * Backend remains the sole authority for member redaction, product
 * visibility, outlet enforcement, commercial totals and sale_reference.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Search, Plus, ArrowLeft, UserPlus, User, CheckCircle2, X,
  Package, Ticket, ChevronRight, LogOut, Home, Loader2, Building2,
  RefreshCw,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import LightVoucherPreview from '../components/loyalty/LightVoucherPreview';

const INR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', {
  minimumFractionDigits: 0, maximumFractionDigits: 2,
})}`;

// --- Views ---
const VIEW_OUTLET_PICK = 'outlet_pick';   // NEW · mandatory outlet gate
const VIEW_HOME        = 'home';
const VIEW_CREATE      = 'create_member';
const VIEW_KIT_PICK    = 'kit_pick';
const VIEW_KIT_REVIEW  = 'kit_review';
const VIEW_VOU_PICK    = 'voucher_pick';
const VIEW_VOU_REVIEW  = 'voucher_review';
const VIEW_DONE        = 'done';

const ADMIN_LIKE = new Set(['admin', 'area_manager', 'super_admin']);
const LS_KEY_PREFIX = 'restropulse_sales_outlet::';


export default function LoyaltySalesWorkspace() {
  const { user, getHeaders, API, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Outlet context (source of truth for all subsequent calls).
  const [outlets, setOutlets]           = useState([]);           // authorised outlets
  const [outletsLoaded, setOutletsLoaded] = useState(false);
  const [selectedOutlet, setSelectedOutlet] = useState(null);     // {id, name, portfolio_id, ...}

  const [view, setView]           = useState(VIEW_OUTLET_PICK);
  const [member, setMember]       = useState(null);
  const [kits, setKits]           = useState([]);
  const [voucherTypes, setVouchers] = useState([]);
  const [pickedKit, setPickedKit] = useState(null);
  const [voucherCart, setVoucherCart] = useState([]);
  const [saleResult, setSaleResult]   = useState(null);

  // Compute the persistence key once per user.
  const lsKey = useMemo(
    () => `${LS_KEY_PREFIX}${user?.id || user?.email || 'anon'}`,
    [user?.id, user?.email],
  );

  // ---- Guard: refuse to render for wrong roles.
  const allowed = ['admin', 'area_manager', 'super_admin', 'loyalty_sales'];
  const isAllowed = user && allowed.includes(user.role);

  // Reset checkout state when the selected outlet changes.
  const clearCheckout = useCallback(() => {
    setPickedKit(null);
    setVoucherCart([]);
    setSaleResult(null);
    setKits([]);
    setVouchers([]);
  }, []);

  // ---- Load the authorised outlet list once on mount. ----
  useEffect(() => {
    if (!isAllowed) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/outlets`, { headers: getHeaders() });
        const list = Array.isArray(r.data) ? r.data : [];
        setOutlets(list);
      } catch (e) {
        toast.error('Could not load your outlets');
        setOutlets([]);
      } finally {
        setOutletsLoaded(true);
      }
    })();
  }, [isAllowed]);

  // ---- Resolve the selected outlet: URL > localStorage > single > none. ----
  useEffect(() => {
    if (!outletsLoaded) return;
    if (outlets.length === 0) {
      setSelectedOutlet(null);
      setView(VIEW_OUTLET_PICK);
      return;
    }

    const urlOutletId = searchParams.get('outlet_id') || '';
    const lsOutletId = (() => {
      try { return localStorage.getItem(lsKey) || ''; } catch { return ''; }
    })();

    const validate = (id) => outlets.find((o) => o.id === id) || null;

    // Priority: URL > localStorage > (single outlet auto-select).
    let picked = validate(urlOutletId)
      || validate(lsOutletId)
      || (outlets.length === 1 ? outlets[0] : null);

    if (picked) {
      setSelectedOutlet(picked);
      // Reflect back to URL + storage for stability.
      if (searchParams.get('outlet_id') !== picked.id) {
        const next = new URLSearchParams(searchParams);
        next.set('outlet_id', picked.id);
        setSearchParams(next, { replace: true });
      }
      try { localStorage.setItem(lsKey, picked.id); } catch { /* ignore */ }
      setView(VIEW_HOME);
    } else {
      setSelectedOutlet(null);
      setView(VIEW_OUTLET_PICK);
      // Strip any invalid outlet_id from URL to prevent confusion.
      if (urlOutletId) {
        const next = new URLSearchParams(searchParams);
        next.delete('outlet_id');
        setSearchParams(next, { replace: true });
      }
      // Clean stale storage
      if (lsOutletId && !validate(lsOutletId)) {
        try { localStorage.removeItem(lsKey); } catch { /* ignore */ }
      }
    }
  }, [outletsLoaded, outlets, lsKey]);

  // Explicit outlet selection (from OutletSelectionView).
  const handleOutletSelected = useCallback((o) => {
    setSelectedOutlet(o);
    const next = new URLSearchParams(searchParams);
    next.set('outlet_id', o.id);
    setSearchParams(next, { replace: false });
    try { localStorage.setItem(lsKey, o.id); } catch { /* ignore */ }
    clearCheckout();
    setView(VIEW_HOME);
  }, [searchParams, setSearchParams, lsKey, clearCheckout]);

  const handleChangeOutlet = useCallback(() => {
    clearCheckout();
    setView(VIEW_OUTLET_PICK);
  }, [clearCheckout]);

  // Reset back to home + clear picks after a completed sale.
  const backHome = useCallback(() => {
    setView(VIEW_HOME);
    setPickedKit(null);
    setVoucherCart([]);
    setSaleResult(null);
    // Clear the member after a completed sale (default per spec).
    setMember(null);
  }, []);

  const handleTopExit = useCallback(() => {
    // Admin-family: back to their normal dashboard.
    if (ADMIN_LIKE.has(user?.role)) {
      navigate('/');
    } else {
      // loyalty_sales: back to Sales Home (in-flow, no logout).
      backHome();
    }
  }, [user?.role, navigate, backHome]);

  if (!isAllowed) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center p-6 bg-slate-900 text-white">
        <div className="text-center">
          <div className="text-lg font-semibold mb-2">Access restricted</div>
          <div className="text-sm text-slate-300 mb-4">
            The Loyalty Sales Workspace is available to authorised roles only.
          </div>
          <Button variant="outline" onClick={() => navigate('/')} className="bg-transparent border-white/30 text-white">
            Back to home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-slate-950 text-white pb-24"
         data-testid="loyalty-sales-workspace">
      <TopBar
        role={user?.role}
        selectedOutlet={selectedOutlet}
        onExit={handleTopExit}
        onHome={backHome}
        onChangeOutlet={handleChangeOutlet}
        onLogout={logout}
      />

      <div className="max-w-md mx-auto px-4 pt-4">
        {view === VIEW_OUTLET_PICK && (
          <OutletSelectionView
            loaded={outletsLoaded}
            outlets={outlets}
            onPick={handleOutletSelected}
          />
        )}

        {view === VIEW_HOME && selectedOutlet && (
          <HomeView
            outlet={selectedOutlet}
            member={member}
            onSelectMember={setMember}
            onClearMember={() => setMember(null)}
            onCreateNew={() => setView(VIEW_CREATE)}
            onSellKit={() => setView(VIEW_KIT_PICK)}
            onSellVoucher={() => setView(VIEW_VOU_PICK)}
          />
        )}

        {view === VIEW_CREATE && selectedOutlet && (
          <CreateMemberView
            outlet={selectedOutlet}
            onCancel={() => setView(VIEW_HOME)}
            onCreated={(m) => { setMember(m); setView(VIEW_HOME); }}
          />
        )}

        {view === VIEW_KIT_PICK && selectedOutlet && (
          <KitCatalogueView
            outlet={selectedOutlet}
            kits={kits} setKits={setKits}
            onBack={() => setView(VIEW_HOME)}
            onPick={(k) => { setPickedKit(k); setView(VIEW_KIT_REVIEW); }}
          />
        )}

        {view === VIEW_KIT_REVIEW && pickedKit && member && selectedOutlet && (
          <KitReviewView
            outlet={selectedOutlet}
            kit={pickedKit}
            member={member}
            onBack={() => setView(VIEW_KIT_PICK)}
            onConfirmed={(resp) => { setSaleResult({ ...resp, kind: 'kit' }); setView(VIEW_DONE); }}
          />
        )}

        {view === VIEW_VOU_PICK && selectedOutlet && (
          <VoucherCatalogueView
            outlet={selectedOutlet}
            voucherTypes={voucherTypes} setVouchers={setVouchers}
            cart={voucherCart} setCart={setVoucherCart}
            onBack={() => setView(VIEW_HOME)}
            onReview={() => setView(VIEW_VOU_REVIEW)}
          />
        )}

        {view === VIEW_VOU_REVIEW && member && selectedOutlet && (
          <VoucherReviewView
            outlet={selectedOutlet}
            member={member}
            cart={voucherCart}
            onBack={() => setView(VIEW_VOU_PICK)}
            onConfirmed={(resp) => { setSaleResult({ ...resp, kind: 'voucher' }); setView(VIEW_DONE); }}
          />
        )}

        {view === VIEW_DONE && saleResult && (
          <SuccessView result={saleResult} onDone={backHome} />
        )}
      </div>
    </div>
  );
}


// ============================================================
//  Top bar
// ============================================================

function TopBar({ role, selectedOutlet, onExit, onHome, onChangeOutlet, onLogout }) {
  const isAdminLike = ADMIN_LIKE.has(role);
  return (
    <div className="sticky top-0 z-10 bg-slate-950/95 backdrop-blur border-b border-slate-800">
      <div className="max-w-md mx-auto px-4 py-2.5 flex items-center justify-between gap-2">
        <button className="flex items-center gap-2 min-w-0"
                onClick={onExit}
                data-testid="sales-exit-btn"
                aria-label={isAdminLike ? 'Back to RestroPulse' : 'Sales Home'}>
          <ArrowLeft className="w-4 h-4 text-slate-400 shrink-0" />
          <div className="text-left min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-amber-400">
              Loyalty Sales
            </div>
            <div className="text-[12px] text-slate-200 leading-tight truncate">
              {isAdminLike ? 'Back to RestroPulse' : 'Sales Home'}
            </div>
          </div>
        </button>
        <div className="flex items-center gap-1">
          {selectedOutlet && (
            <button
              onClick={onChangeOutlet}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-slate-700 bg-slate-900 hover:bg-slate-800 text-slate-200 text-[11px]"
              data-testid="sales-change-outlet-btn"
              aria-label="Change outlet">
              <Building2 className="w-3.5 h-3.5 text-amber-400" />
              <span className="truncate max-w-[90px]">{selectedOutlet.name}</span>
              <RefreshCw className="w-3 h-3 text-slate-500" />
            </button>
          )}
          <Button size="sm" variant="ghost"
                  className="text-slate-500 hover:text-white hover:bg-slate-800 h-8 w-8 p-0"
                  onClick={onLogout}
                  data-testid="sales-logout-btn"
                  aria-label="Logout">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </div>
      {selectedOutlet && (
        <div className="max-w-md mx-auto px-4 pb-2 -mt-0.5 text-[11px] text-slate-400"
             data-testid="sales-selling-at">
          Selling at <span className="text-slate-200 font-medium">{selectedOutlet.name}</span>
        </div>
      )}
    </div>
  );
}


// ============================================================
//  Outlet selection (mandatory entry gate for multi-outlet users)
// ============================================================

function OutletSelectionView({ loaded, outlets, onPick }) {
  if (!loaded) {
    return (
      <div className="text-center py-12 text-slate-500" data-testid="outlet-pick-loading">
        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-3" />
        Loading your outlets…
      </div>
    );
  }
  if (outlets.length === 0) {
    return (
      <div className="text-center py-12" data-testid="outlet-pick-empty">
        <Building2 className="w-10 h-10 text-slate-600 mx-auto mb-3" />
        <div className="text-base font-semibold text-white mb-1">No outlet assigned</div>
        <div className="text-sm text-slate-400 max-w-xs mx-auto">
          Your account is not assigned to any outlet. Ask an Admin to assign at least one outlet before you can start selling.
        </div>
      </div>
    );
  }

  // Group by portfolio when available.
  const groups = {};
  for (const o of outlets) {
    const key = o.portfolio_id || 'ungrouped';
    (groups[key] = groups[key] || []).push(o);
  }
  const groupKeys = Object.keys(groups);
  const showGroups = groupKeys.length > 1;

  return (
    <div className="space-y-4" data-testid="outlet-pick-view">
      <div>
        <div className="text-lg font-semibold text-white">Select Business Unit</div>
        <div className="text-xs text-slate-400 mt-0.5">
          Choose the outlet you&apos;re selling at. You can change this later.
        </div>
      </div>
      <div className="space-y-4">
        {(showGroups ? groupKeys : ['single']).map((gk, gi) => {
          const list = showGroups ? groups[gk] : outlets;
          const portfolioName = showGroups
            ? (list[0]?.portfolio_name || `Portfolio ${gi + 1}`)
            : null;
          return (
            <div key={gk}>
              {portfolioName && (
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2 px-1">
                  {portfolioName}
                </div>
              )}
              <div className="space-y-2">
                {list.map((o) => (
                  <button
                    key={o.id}
                    onClick={() => onPick(o)}
                    className="w-full text-left p-4 rounded-lg bg-slate-900 border border-slate-800 hover:border-amber-500/50 transition"
                    data-testid={`outlet-pick-${o.id}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-medium text-white truncate">{o.name}</div>
                        {o.city && (
                          <div className="text-xs text-slate-500 mt-0.5">{o.city}</div>
                        )}
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-500 shrink-0" />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ============================================================
//  Home view (search / create / sell)
// ============================================================

function HomeView({ outlet, member, onSelectMember, onClearMember,
                    onCreateNew, onSellKit, onSellVoucher }) {
  const { getHeaders, API } = useAuth();
  const [q, setQ]                 = useState('');
  const [results, setResults]     = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (member) { setResults([]); return; }
    const s = (q || '').trim();
    if (s.length < 2) { setResults([]); return; }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(
          `${API}/sales/loyalty/members/search?q=${encodeURIComponent(s)}&limit=15`,
          { headers: getHeaders() },
        );
        if (!cancelled) setResults(Array.isArray(r.data) ? r.data : []);
      } catch (e) {
        if (!cancelled) toast.error('Search failed');
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, member, API, getHeaders]);

  return (
    <div className="space-y-4" data-testid="sales-home">
      {!member && (
        <>
          <div>
            <Label className="text-xs text-slate-400 uppercase tracking-wider">
              Search member
            </Label>
            <div className="relative mt-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Name, phone, or member ID"
                className="pl-9 h-12 bg-slate-900 border-slate-800 text-white placeholder:text-slate-600 text-base"
                data-testid="sales-member-search"
                autoComplete="off"
                inputMode="search"
              />
            </div>
          </div>

          {searching && (
            <div className="flex items-center justify-center py-4 text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Searching…
            </div>
          )}

          {!searching && results.length > 0 && (
            <div className="space-y-2" data-testid="sales-search-results">
              {results.map((m) => (
                <button
                  key={m.id}
                  className="w-full text-left p-4 rounded-lg bg-slate-900 border border-slate-800 hover:border-amber-500/50 transition"
                  onClick={() => { onSelectMember(m); setResults([]); setQ(''); }}
                  data-testid={`sales-result-${m.id}`}>
                  <div className="font-medium text-white">{m.name}</div>
                  <div className="text-xs text-slate-400 mt-0.5">
                    {m.phone_masked || '—'}
                    {m.email_masked && <> · {m.email_masked}</>}
                  </div>
                  <div className="text-[10px] text-slate-500 mt-1 font-mono">
                    {m.member_id_public}
                  </div>
                </button>
              ))}
            </div>
          )}

          {!searching && q.trim().length >= 2 && results.length === 0 && (
            <div className="text-sm text-slate-500 text-center py-2">
              No members matched. Create a new member below.
            </div>
          )}

          <Button className="w-full h-12 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
                  onClick={onCreateNew}
                  data-testid="sales-create-member-btn">
            <UserPlus className="w-4 h-4 mr-2" />
            Create Member
          </Button>

          <div className="pt-8 text-center text-slate-500 text-sm border-t border-slate-800">
            <User className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <div className="font-medium">No member selected</div>
            <div className="text-xs mt-1">
              Search or create a member to begin a sale.
            </div>
          </div>
        </>
      )}

      {member && (
        <>
          <div className="rounded-xl bg-gradient-to-br from-amber-500/10 to-slate-900 border border-amber-500/30 p-5"
               data-testid="sales-selected-member">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-lg font-semibold text-white truncate">
                  {member.name}
                </div>
                <div className="text-sm text-slate-300 mt-1">
                  {member.phone_masked || '—'}
                </div>
                {member.email_masked && (
                  <div className="text-xs text-slate-400 mt-0.5">
                    {member.email_masked}
                  </div>
                )}
                <div className="text-[11px] text-slate-500 mt-2 font-mono">
                  {member.member_id_public}
                </div>
                {/* P0 · A member profile is NOT a purchased membership.
                    Show neutral copy — never "Active Member" here. The
                    admin's Member 360 remains authoritative for card
                    state; the mobile Sales UI does not fabricate one. */}
                <Badge variant="outline"
                       className="mt-2 border-slate-500/40 text-slate-300 bg-slate-500/10 text-[10px]"
                       data-testid="sales-member-profile-badge">
                  Member Profile · Ready for Membership Sale
                </Badge>
              </div>
              <button
                onClick={onClearMember}
                className="text-slate-500 hover:text-white p-1"
                data-testid="sales-clear-member-btn"
                aria-label="Change member">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="space-y-3">
            <Button className="w-full h-14 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold text-base justify-between"
                    onClick={onSellKit}
                    data-testid="sales-sell-kit-btn">
              <span className="flex items-center gap-2">
                <Package className="w-5 h-5" />
                Sell Membership Kit
              </span>
              <ChevronRight className="w-5 h-5" />
            </Button>
            <Button className="w-full h-14 bg-slate-800 hover:bg-slate-700 text-white font-semibold text-base justify-between border border-slate-700"
                    onClick={onSellVoucher}
                    data-testid="sales-sell-voucher-btn">
              <span className="flex items-center gap-2">
                <Ticket className="w-5 h-5" />
                Sell Voucher
              </span>
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </>
      )}
    </div>
  );
}


// ============================================================
//  Create member
// ============================================================

function CreateMemberView({ outlet, onCancel, onCreated }) {
  const { getHeaders, API } = useAuth();
  const [name, setName]     = useState('');
  const [phone, setPhone]   = useState('');
  const [email, setEmail]   = useState('');
  const [busy, setBusy]     = useState(false);

  const submit = async () => {
    if (!name.trim() || !phone.trim() || !email.trim()) {
      toast.error('Name, phone and email are required');
      return;
    }
    setBusy(true);
    try {
      // P0 · Always stamp primary_outlet_id = selected sales outlet.
      // Backend validates that this outlet exists in tenant AND is
      // inside a sales actor's assigned scope; it is persisted on the
      // member as origin metadata (members remain tenant-level).
      const r = await axios.post(`${API}/admin/loyalty/members`,
        {
          name: name.trim(),
          phone: phone.trim(),
          email: email.trim(),
          primary_outlet_id: outlet.id,
        },
        { headers: getHeaders() });
      toast.success('Member created');
      onCreated(r.data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === 'string' ? d : (d?.message || 'Create failed');
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="sales-create-member-view">
      <BackHeader title="Create Member" onBack={onCancel} />
      <div className="space-y-3">
        <div>
          <Label className="text-xs text-slate-400">Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)}
                 className="mt-1 h-12 bg-slate-900 border-slate-800 text-white text-base"
                 data-testid="sales-create-name" />
        </div>
        <div>
          <Label className="text-xs text-slate-400">Phone</Label>
          <Input value={phone} onChange={(e) => setPhone(e.target.value)}
                 inputMode="tel" type="tel"
                 className="mt-1 h-12 bg-slate-900 border-slate-800 text-white text-base"
                 data-testid="sales-create-phone" />
        </div>
        <div>
          <Label className="text-xs text-slate-400">Email</Label>
          <Input value={email} onChange={(e) => setEmail(e.target.value)}
                 inputMode="email" type="email"
                 className="mt-1 h-12 bg-slate-900 border-slate-800 text-white text-base"
                 data-testid="sales-create-email" />
        </div>
      </div>
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-slate-950/95 backdrop-blur border-t border-slate-800">
        <div className="max-w-md mx-auto">
          <Button className="w-full h-12 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
                  onClick={submit} disabled={busy}
                  data-testid="sales-create-submit">
            {busy ? 'Creating…' : 'Create & Continue'}
          </Button>
        </div>
      </div>
    </div>
  );
}


function BackHeader({ title, onBack }) {
  return (
    <div className="flex items-center gap-3 -mx-1">
      <button onClick={onBack}
              className="p-1.5 -ml-1.5 text-slate-400 hover:text-white"
              data-testid="sales-back-btn"
              aria-label="Back">
        <ArrowLeft className="w-5 h-5" />
      </button>
      <div className="text-base font-semibold text-white">{title}</div>
    </div>
  );
}


// ============================================================
//  Kit catalogue / review
// ============================================================

function KitCatalogueView({ outlet, kits, setKits, onBack, onPick }) {
  const { getHeaders, API } = useAuth();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/sales/loyalty/kits?outlet_id=${encodeURIComponent(outlet.id)}`,
              { headers: getHeaders() })
      .then((r) => setKits(Array.isArray(r.data) ? r.data : []))
      .catch(() => toast.error('Could not load memberships'))
      .finally(() => setLoading(false));
  }, [API, getHeaders, setKits, outlet.id]);

  return (
    <div className="space-y-4" data-testid="sales-kit-catalogue">
      <BackHeader title="Sell Membership" onBack={onBack} />

      {loading && <SkeletonList />}
      {!loading && kits.length === 0 && (
        <EmptyCatalog
          outletName={outlet.name}
          productLabel="memberships"
          testId="sales-kit-empty"
        />
      )}

      <div className="space-y-3">
        {kits.map((k) => (
          <Card key={k.id}
                className="p-5 bg-gradient-to-br from-slate-900 to-slate-950 border-slate-800 text-white"
                data-testid={`sales-kit-card-${k.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-lg font-bold uppercase tracking-wider">
                  {k.name}
                </div>
                {k.tier && (
                  <Badge variant="outline"
                         className="mt-1 text-[10px] border-amber-400/50 text-amber-300 bg-amber-500/10">
                    {k.tier}
                  </Badge>
                )}
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-amber-400">
                  {INR(k.price)}
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  Valid {k.validity_days} days
                </div>
              </div>
            </div>
            {k.description && (
              <div className="text-sm text-slate-400 mt-2 leading-snug">
                {k.description}
              </div>
            )}
            {(k.voucher_composition || []).length > 0 && (
              <div className="mt-3 border-t border-slate-800 pt-3 space-y-1">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">
                  Includes
                </div>
                {(k.voucher_composition || []).map((line, i) => (
                  <div key={i} className="text-xs text-slate-300 flex items-center gap-2">
                    <span className="text-amber-400">•</span>
                    <span>{line.count} × voucher</span>
                  </div>
                ))}
              </div>
            )}
            <Button className="w-full mt-4 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold h-11"
                    onClick={() => onPick(k)}
                    data-testid={`sales-kit-select-${k.id}`}>
              Select
            </Button>
          </Card>
        ))}
      </div>
    </div>
  );
}


function KitReviewView({ outlet, kit, member, onBack, onConfirmed }) {
  const { getHeaders, API } = useAuth();
  const [payMethod, setPayMethod]       = useState('cash');
  const [payReference, setPayReference] = useState('');
  const [busy, setBusy]                 = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      // P0 · The billing outlet is the EXPLICITLY selected sales outlet
      // — never derived from /auth/me. Server re-validates against the
      // sales actor's assigned_outlet_ids (see _resolve_sale_outlet_id).
      const r = await axios.post(`${API}/admin/loyalty/kit-purchase`, {
        member_id: member.id,
        kit_id:    kit.id,
        outlet_id: outlet.id,
        payment_method: payMethod,
        payment_reference: payReference || undefined,
      }, { headers: getHeaders() });
      onConfirmed({
        sale_reference: r.data?.sale_reference,
        amount:         kit.price,
        product_name:   kit.name,
        payment_method: payMethod,
        member_name:    member.name,
        outlet_name:    outlet.name,
      });
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === 'string' ? d : (d?.message || 'Sale failed');
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4 pb-24" data-testid="sales-kit-review">
      <BackHeader title="Review Membership Sale" onBack={onBack} />

      <SummaryCard rows={[
        { k: 'Selling at', v: outlet.name,          testid: 'review-outlet' },
        { k: 'Customer',   v: member.name,          testid: 'review-customer' },
        { k: 'Membership', v: kit.name,             testid: 'review-product' },
        { k: 'Price',      v: INR(kit.price),
          highlight: true, testid: 'review-price' },
        { k: 'Validity',   v: `${kit.validity_days} days`, testid: 'review-validity' },
      ]} />

      {(kit.voucher_composition || []).length > 0 && (
        <div className="rounded-lg border border-slate-800 bg-slate-900 p-4">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">
            Included Benefits
          </div>
          <div className="space-y-1 text-sm text-slate-200">
            {(kit.voucher_composition || []).map((line, i) => (
              <div key={i} className="flex items-center justify-between">
                <span>{line.count} × voucher</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-lg border border-slate-800 bg-slate-900 p-4 space-y-3">
        <div>
          <Label className="text-xs text-slate-400">Payment Method</Label>
          <Select value={payMethod} onValueChange={setPayMethod}>
            <SelectTrigger className="mt-1 h-11 bg-slate-950 border-slate-800 text-white"
                           data-testid="review-payment-method">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="cash">Cash</SelectItem>
              <SelectItem value="upi">UPI</SelectItem>
              <SelectItem value="card">Card</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-slate-400">Payment Reference (optional)</Label>
          <Input value={payReference} onChange={(e) => setPayReference(e.target.value)}
                 className="mt-1 h-11 bg-slate-950 border-slate-800 text-white"
                 placeholder="Transaction ID / UPI ref"
                 data-testid="review-payment-reference" />
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 p-4 bg-slate-950/95 backdrop-blur border-t border-slate-800">
        <div className="max-w-md mx-auto">
          <Button className="w-full h-12 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
                  onClick={submit} disabled={busy}
                  data-testid="review-confirm-btn">
            {busy ? 'Confirming…' : `Confirm Sale · ${INR(kit.price)}`}
          </Button>
        </div>
      </div>
    </div>
  );
}


// ============================================================
//  Voucher catalogue / review
// ============================================================

function VoucherCatalogueView({ outlet, voucherTypes, setVouchers, cart, setCart, onBack, onReview }) {
  const { getHeaders, API } = useAuth();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/sales/loyalty/voucher-types?outlet_id=${encodeURIComponent(outlet.id)}`,
              { headers: getHeaders() })
      .then((r) => setVouchers(Array.isArray(r.data) ? r.data : []))
      .catch(() => toast.error('Could not load vouchers'))
      .finally(() => setLoading(false));
  }, [API, getHeaders, setVouchers, outlet.id]);

  const inCart = (id) => cart.find((c) => c.voucher_type.id === id);

  const bump = (v, delta) => {
    setCart((prev) => {
      const existing = prev.find((x) => x.voucher_type.id === v.id);
      if (!existing) {
        return delta > 0 ? [...prev, { voucher_type: v, count: 1 }] : prev;
      }
      const next = Math.max(0, existing.count + delta);
      if (next === 0) return prev.filter((x) => x.voucher_type.id !== v.id);
      return prev.map((x) => x.voucher_type.id === v.id ? { ...x, count: next } : x);
    });
  };

  const cartCount = cart.reduce((s, x) => s + x.count, 0);

  return (
    <div className="space-y-4 pb-28" data-testid="sales-voucher-catalogue">
      <BackHeader title="Sell Voucher" onBack={onBack} />
      {loading && <SkeletonList />}
      {!loading && voucherTypes.length === 0 && (
        <EmptyCatalog
          outletName={outlet.name}
          productLabel="vouchers"
          testId="sales-voucher-empty"
        />
      )}

      <div className="space-y-3">
        {voucherTypes.map((v) => {
          const inCartLine = inCart(v.id);
          return (
            <Card key={v.id}
                  className="p-4 bg-slate-900 border-slate-800 text-white"
                  data-testid={`sales-voucher-card-${v.id}`}>
              <div className="flex gap-3 items-start">
                <div className="shrink-0">
                  <LightVoucherPreview voucher={v} width={72} height={96} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-white truncate">{v.name}</div>
                  <div className="text-xs text-slate-400 mt-0.5">
                    <BenefitPill v={v} />
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1">
                    Valid {v.validity_days} days
                  </div>
                </div>
              </div>

              <div className="mt-3 flex items-center justify-between">
                <div className="text-xs text-slate-400">Quantity</div>
                <div className="flex items-center gap-3">
                  <Button size="sm" variant="outline"
                          className="w-9 h-9 p-0 bg-slate-800 border-slate-700 text-white"
                          onClick={() => bump(v, -1)}
                          disabled={!inCartLine}
                          data-testid={`sales-voucher-minus-${v.id}`}>
                    –
                  </Button>
                  <div className="min-w-[24px] text-center font-semibold"
                       data-testid={`sales-voucher-qty-${v.id}`}>
                    {inCartLine?.count || 0}
                  </div>
                  <Button size="sm"
                          className="w-9 h-9 p-0 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold"
                          onClick={() => bump(v, +1)}
                          data-testid={`sales-voucher-plus-${v.id}`}>
                    +
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {cartCount > 0 && (
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-slate-950/95 backdrop-blur border-t border-slate-800">
          <div className="max-w-md mx-auto">
            <Button className="w-full h-12 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
                    onClick={onReview}
                    data-testid="sales-voucher-review-btn">
              Review {cartCount} voucher{cartCount === 1 ? '' : 's'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}


function VoucherReviewView({ outlet, member, cart, onBack, onConfirmed }) {
  const { getHeaders, API } = useAuth();
  const [payMethod, setPayMethod]       = useState('cash');
  const [payReference, setPayReference] = useState('');
  const [pricePaid, setPricePaid]       = useState('');
  const [busy, setBusy]                 = useState(false);

  const submit = async () => {
    if (!cart.length) { toast.error('Add at least one voucher'); return; }
    if (pricePaid === '' || Number.isNaN(Number(pricePaid))) {
      toast.error('Enter the amount collected'); return;
    }
    setBusy(true);
    try {
      const lines = cart.map((c) => ({
        voucher_type_id: c.voucher_type.id,
        count: c.count,
      }));
      // P0 · outlet_id is always the explicitly selected sales outlet.
      const r = await axios.post(`${API}/admin/loyalty/voucher-sale`, {
        member_id: member.id,
        outlet_id: outlet.id,
        lines,
        price_paid: Number(pricePaid),
        payment_method: payMethod,
        payment_reference: payReference || undefined,
      }, { headers: getHeaders() });
      const totalVouchers = r.data?.vouchers_issued || cart.reduce((s, c) => s + c.count, 0);
      onConfirmed({
        sale_reference: r.data?.sale_reference,
        amount:         Number(pricePaid),
        product_name:   `${totalVouchers} voucher${totalVouchers === 1 ? '' : 's'}`,
        payment_method: payMethod,
        member_name:    member.name,
        outlet_name:    outlet.name,
      });
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === 'string' ? d : (d?.message || 'Sale failed');
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4 pb-28" data-testid="sales-voucher-review">
      <BackHeader title="Review Voucher Sale" onBack={onBack} />

      <div className="rounded-lg border border-slate-800 bg-slate-900 p-4">
        <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">
          Selling at
        </div>
        <div className="font-semibold text-white" data-testid="review-voucher-outlet">
          {outlet.name}
        </div>
      </div>

      <div className="rounded-lg border border-slate-800 bg-slate-900 p-4">
        <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">
          Customer
        </div>
        <div className="font-semibold text-white">{member.name}</div>
      </div>

      <div className="rounded-lg border border-slate-800 bg-slate-900 p-4 space-y-2">
        <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">
          Items
        </div>
        {cart.map((c) => (
          <div key={c.voucher_type.id}
               className="flex items-center justify-between text-sm text-slate-200"
               data-testid={`review-item-${c.voucher_type.id}`}>
            <span>{c.voucher_type.name}</span>
            <span className="font-mono text-slate-400">× {c.count}</span>
          </div>
        ))}
      </div>

      <div className="rounded-lg border border-slate-800 bg-slate-900 p-4 space-y-3">
        <div>
          <Label className="text-xs text-slate-400">Amount collected (₹) *</Label>
          <Input type="number" min="0" inputMode="decimal"
                 value={pricePaid} onChange={(e) => setPricePaid(e.target.value)}
                 className="mt-1 h-11 bg-slate-950 border-slate-800 text-white text-base"
                 data-testid="review-price-paid" />
        </div>
        <div>
          <Label className="text-xs text-slate-400">Payment Method</Label>
          <Select value={payMethod} onValueChange={setPayMethod}>
            <SelectTrigger className="mt-1 h-11 bg-slate-950 border-slate-800 text-white"
                           data-testid="review-voucher-payment-method">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="cash">Cash</SelectItem>
              <SelectItem value="upi">UPI</SelectItem>
              <SelectItem value="card">Card</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-slate-400">Payment Reference (optional)</Label>
          <Input value={payReference} onChange={(e) => setPayReference(e.target.value)}
                 className="mt-1 h-11 bg-slate-950 border-slate-800 text-white"
                 data-testid="review-voucher-payment-reference" />
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 p-4 bg-slate-950/95 backdrop-blur border-t border-slate-800">
        <div className="max-w-md mx-auto">
          <Button className="w-full h-12 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
                  onClick={submit} disabled={busy}
                  data-testid="review-voucher-confirm-btn">
            {busy ? 'Confirming…' : 'Confirm Sale'}
          </Button>
        </div>
      </div>
    </div>
  );
}


// ============================================================
//  Success / atoms
// ============================================================

function SuccessView({ result, onDone }) {
  return (
    <div className="text-center py-6" data-testid="sales-success">
      <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center mb-4">
        <CheckCircle2 className="w-8 h-8 text-emerald-400" />
      </div>
      <div className="text-xl font-bold text-white mb-1">Sale Completed</div>
      <div className="text-xs text-slate-500 mb-6">
        {new Date().toLocaleString()}
      </div>

      <div className="rounded-lg border border-slate-800 bg-slate-900 p-4 text-left space-y-3 max-w-sm mx-auto">
        <Row label="Reference"
             value={result.sale_reference || '—'}
             mono
             testid="success-reference" />
        {result.outlet_name && (
          <Row label="Outlet" value={result.outlet_name} testid="success-outlet" />
        )}
        <Row label="Customer" value={result.member_name} testid="success-customer" />
        <Row label="Product"  value={result.product_name} testid="success-product" />
        <Row label="Amount"   value={INR(result.amount)}
             highlight testid="success-amount" />
        <Row label="Payment"  value={result.payment_method?.toUpperCase()}
             testid="success-payment" />
      </div>

      <Button className="mt-6 w-full max-w-sm mx-auto h-12 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
              onClick={onDone}
              data-testid="success-done-btn">
        <Home className="w-4 h-4 mr-2" />
        Done
      </Button>
    </div>
  );
}


function Row({ label, value, mono, highlight, testid }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <div className="text-slate-500">{label}</div>
      <div className={`${mono ? 'font-mono' : ''} ${highlight ? 'text-amber-400 font-bold' : 'text-slate-100'}`}
           data-testid={testid}>
        {value || '—'}
      </div>
    </div>
  );
}


function SummaryCard({ rows }) {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900 p-4 space-y-3">
      {rows.map((r, i) => (
        <div key={i} className="flex items-center justify-between text-sm">
          <div className="text-slate-500">{r.k}</div>
          <div className={r.highlight ? 'text-amber-400 font-bold text-base' : 'text-slate-100 font-medium'}
               data-testid={r.testid}>
            {r.v}
          </div>
        </div>
      ))}
    </div>
  );
}


function SkeletonList() {
  return (
    <div className="space-y-3">
      {[1, 2, 3].map((i) => (
        <div key={i} className="h-32 rounded-lg bg-slate-900/50 border border-slate-800 animate-pulse" />
      ))}
    </div>
  );
}


function EmptyCatalog({ outletName, productLabel, testId }) {
  const label = productLabel === 'vouchers' ? 'vouchers' : 'memberships';
  return (
    <div className="text-center py-10 text-slate-400 max-w-xs mx-auto" data-testid={testId}>
      <Ticket className="w-10 h-10 mx-auto mb-3 opacity-30" />
      <div className="text-sm font-medium text-slate-200 mb-1">
        No {label} enabled for Sales at {outletName}
      </div>
      <div className="text-xs text-slate-500 leading-relaxed">
        An Admin must enable &ldquo;Available to Sales Team&rdquo; on the
        {productLabel === 'vouchers' ? ' Voucher' : ' Membership Plan'} before it can be sold here.
      </div>
    </div>
  );
}


function BenefitPill({ v }) {
  const t = v.benefit_type;
  const val = v.benefit_value;
  if (t === 'percent_discount') return <span className="text-amber-300">{val}% off</span>;
  if (t === 'flat_discount')    return <span className="text-amber-300">{INR(val)} off</span>;
  if (t === 'complimentary_item') return <span className="text-amber-300">Complimentary item</span>;
  if (t === 'buy_one_get_one')  return <span className="text-amber-300">Buy 1 Get 1</span>;
  if (t === 'cashback')         return <span className="text-amber-300">{INR(val)} cashback</span>;
  return <span className="text-slate-400">{t}</span>;
}
